import { useEffect } from 'react';

const ABSOLUTE = (url) => {
  if (!url) return undefined;
  if (url.startsWith('http')) return url;
  if (typeof window === 'undefined') return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const setMeta = (selector, attr, value) => {
  if (typeof document === 'undefined' || !value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, attrName, attrVal] = selector.match(/\[(\w+)="(.+?)"\]/) || [];
    if (attrName && attrVal) el.setAttribute(attrName, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setCanonical = (url) => {
  if (typeof document === 'undefined' || !url) return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
};

/**
 * Per-page SEO updater. Updates <title>, description, canonical link, and
 * Open Graph + Twitter Card meta tags. Restores the previous values on unmount.
 *
 * Usage: useSEO({ title, description, image, url, type })
 */
export default function useSEO({ title, description, image, url, type = 'website' } = {}) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    // Capture current values so we can restore on unmount
    const prev = {
      title: document.title,
      description: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      ogTitle: document.head.querySelector('meta[property="og:title"]')?.getAttribute('content'),
      ogDesc: document.head.querySelector('meta[property="og:description"]')?.getAttribute('content'),
      ogImage: document.head.querySelector('meta[property="og:image"]')?.getAttribute('content'),
      ogUrl: document.head.querySelector('meta[property="og:url"]')?.getAttribute('content'),
      ogType: document.head.querySelector('meta[property="og:type"]')?.getAttribute('content'),
      twTitle: document.head.querySelector('meta[name="twitter:title"]')?.getAttribute('content'),
      twDesc: document.head.querySelector('meta[name="twitter:description"]')?.getAttribute('content'),
      twImage: document.head.querySelector('meta[name="twitter:image"]')?.getAttribute('content'),
      canonical: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
    };

    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', 'content', description);

    const fullUrl = url || (typeof window !== 'undefined' ? window.location.href : undefined);
    const fullImage = ABSOLUTE(image);

    if (title) {
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }
    if (description) {
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (fullImage) {
      setMeta('meta[property="og:image"]', 'content', fullImage);
      setMeta('meta[name="twitter:image"]', 'content', fullImage);
    }
    if (fullUrl) {
      setMeta('meta[property="og:url"]', 'content', fullUrl);
      setCanonical(fullUrl);
    }
    if (type) setMeta('meta[property="og:type"]', 'content', type);

    return () => {
      // Restore previous values (so navigating back to the landing page picks up the default tags)
      if (prev.title) document.title = prev.title;
      if (prev.description) setMeta('meta[name="description"]', 'content', prev.description);
      if (prev.ogTitle) setMeta('meta[property="og:title"]', 'content', prev.ogTitle);
      if (prev.ogDesc) setMeta('meta[property="og:description"]', 'content', prev.ogDesc);
      if (prev.ogImage) setMeta('meta[property="og:image"]', 'content', prev.ogImage);
      if (prev.ogUrl) setMeta('meta[property="og:url"]', 'content', prev.ogUrl);
      if (prev.ogType) setMeta('meta[property="og:type"]', 'content', prev.ogType);
      if (prev.twTitle) setMeta('meta[name="twitter:title"]', 'content', prev.twTitle);
      if (prev.twDesc) setMeta('meta[name="twitter:description"]', 'content', prev.twDesc);
      if (prev.twImage) setMeta('meta[name="twitter:image"]', 'content', prev.twImage);
      if (prev.canonical) setCanonical(prev.canonical);
    };
  }, [title, description, image, url, type]);
}
